import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Navbar from './Navbar'; // Assuming Navbar component exists
import CancellationPolicy from './CancellationPolicy'; // Assuming CancellationPolicy component exists
import { PHP_BASE_URL } from './apiBase';

// --- THEME AND GLOBAL STYLES --- //
const theme = {
  colors: {
    primary: 'rgba(106, 17, 203, 0.9)',
    secondary: 'rgba(37, 117, 252, 0.9)',
    background: '#0D0D0D',
    card: 'rgba(255, 255, 255, 0.05)',
    text: '#E0E0E0',
    textSecondary: '#BDBDBD',
    success: '#28a745',
    danger: '#dc3545',
  },
  fonts: {
    main: "'Poppins', sans-serif",
  },
};

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
  body {
    background-color: ${({ theme }) => theme.colors.background};
    font-family: ${({ theme }) => theme.fonts.main};
    color: ${({ theme }) => theme.colors.text};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

// --- STYLED COMPONENTS --- //
const PageWrapper = styled.div`
  min-height: 100vh;
  padding-bottom: 50px;
`;

const Header = styled(motion.div)`
  text-align: center;
  padding: 48px 24px;
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const EventImage = styled(motion.img)`
  width: 100%;
  max-width: 350px;
  height: auto;
  aspect-ratio: 1 / 1;
  border-radius: 20px;
  object-fit: cover;
  margin-bottom: 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.1);
  position: relative;
`;

const EventTitle = styled(motion.h1)`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  margin-bottom:20px;
  color: #fff;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.7);
`;

const ContentWrapper = styled(motion.div)`
  max-width: 600px;
  margin: -50px auto 0;
  padding: 0 16px;
  position: relative;
  z-index: 10;
`;

const GlassCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
`;

const PolicyInfoContainer = styled.div`
  text-align: center;
  padding: 0 0 24px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  p {
    margin: 4px 0;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const StyledLink = styled.a`
  color: #8a9ffc;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.3s ease;
  &:hover {
    color: #a8baff;
  }
`;

const TicketRowWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  &:last-child {
    border-bottom: none;
  }
`;

const TicketInfo = styled.div`
  flex: 1;
`;

const TicketName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
`;

const TicketPrice = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
`;

const QtyText = styled.div`
  min-width: 40px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  color: #fff;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px 0 0;
  margin-top: 8px;
`;

const TotalLabel = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
`;

const TotalValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
`;

const CheckboxContainer = styled(motion.label)`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const CustomCheckbox = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  transition: all 0.3s ease;
  background-color: ${({ checked }) => (checked ? 'rgba(106, 17, 203, 1)' : 'transparent')};
  border-color: rgba(106, 17, 203, 1);
`;

const CheckboxLabel = styled.span`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const GradientButton = styled(motion.button)`
  width: 100%;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border: none;
  color: #fff;
  padding: 16px;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  &:not(:disabled):hover {
    box-shadow: 0 0 20px rgba(37, 117, 252, 0.5);
    transform: translateY(-2px);
  }
`;

const QtyButton = styled(GradientButton)`
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  font-size: 1.5rem;
  line-height: 36px;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContainer = styled(motion.div)`
  background: rgba(31, 27, 46, 0.8);
  backdrop-filter: blur(25px);
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #fff;
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;
`;

const ModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const PaymentModalContainer = styled(ModalContainer)`
    width: 100%;
    height: 100%;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
    border: none;
    box-shadow: none;
    background: #000;
    position: relative; // Needed for positioning the loading overlay
`;

const Iframe = styled.iframe`
    flex: 1;
    width: 100%;
    height: 100%;
    border: none;
`;

const CenteredMessageContainer = styled(motion.div)`
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 20px;
`;

const MessageContent = styled.div`
    max-width: 500px;
`;

const IconCircle = styled.div`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto 30px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
    background: ${({ success, theme }) =>
        success
            ? `linear-gradient(to right, ${theme.colors.success}, #218838)`
            : `linear-gradient(to right, ${theme.colors.danger}, #c82333)`
    };
`;

const MessageTitle = styled.h2`
    color: #fff;
    margin-bottom: 20px;
    font-size: 2rem;
`;

const MessageText = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 1.1rem;
    line-height: 1.6;
`;

// New Styled Components for Email Prompt
const EmailInput = styled.input`
    width: 100%;
    padding: 12px;
    background: rgba(0,0,0,0.2);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 8px;
    color: #fff;
    font-size: 1rem;
    margin-top: 16px;
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
    }
`;

const PhoneInput = styled(EmailInput)``;

// --- NEW --- Styling for the loading indicator
const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
`;

const Spinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #fff;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;


// --- ANIMATION VARIANTS --- //
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 },
  },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.3 },
  },
};


// --- COMPONENTS --- //
const TicketRow = ({ ticket, onChange }) => (
  <TicketRowWrapper>
    <TicketInfo>
      <TicketName>{ticket.name}</TicketName>
      <TicketPrice>{ticket.price} €</TicketPrice>
    </TicketInfo>
    <QtyControls>
      <QtyButton
        onClick={() => onChange(ticket.id, ticket.selected - 1)}
        disabled={ticket.selected === 0}
        whileTap={{ scale: 0.9 }}
      >
        −
      </QtyButton>
      <QtyText>{ticket.selected}</QtyText>
      <QtyButton
        onClick={() => onChange(ticket.id, ticket.selected + 1)}
        disabled={ticket.selected >= ticket.quantity}
        whileTap={{ scale: 0.9 }}
      >
        +
      </QtyButton>
    </QtyControls>
  </TicketRowWrapper>
);

const CancellationPolicyModal = ({ visible, onClose }) => (
  <AnimatePresence>
    {visible && (
      <Overlay
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      >
        <ModalContainer variants={modalVariants} onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>Πολιτική Ακυρώσεων & Αλλαγών</ModalTitle>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </ModalHeader>
          <ModalContent>
            <CancellationPolicy />
          </ModalContent>
          <ModalActions>
            <GradientButton onClick={onClose} style={{width: 'auto', padding: '12px 24px'}}>Κλείσιμο</GradientButton>
          </ModalActions>
        </ModalContainer>
      </Overlay>
    )}
  </AnimatePresence>
);

const TermsOfUseModal = ({ visible, onClose }) => (
    <AnimatePresence>
        {visible && (
            <Overlay
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                onClick={onClose}
            >
                <ModalContainer
                    variants={modalVariants}
                    onClick={(e) => e.stopPropagation()}
                    style={{ height: '90%', maxHeight: '1000px' }} // Increased height for the modal
                >
                    <ModalHeader>
                        <ModalTitle>Όροι Χρήσης</ModalTitle>
                        <CloseButton onClick={onClose}>&times;</CloseButton>
                    </ModalHeader>
                    <Iframe src="https://upass.gr/policy.html" title="Terms of Use" />
                    <ModalActions>
                        <GradientButton onClick={onClose} style={{ width: 'auto', padding: '12px 24px' }}>
                            Κλείσιμο
                        </GradientButton>
                    </ModalActions>
                </ModalContainer>
            </Overlay>
        )}
    </AnimatePresence>
);

// --- MODIFIED --- PaymentModal now includes a loading indicator
const PaymentModal = ({ visible, url, onClose }) => {
    const [isIframeLoading, setIsIframeLoading] = useState(true);

    // Reset loading state whenever the modal is opened
    useEffect(() => {
        if (visible) {
            setIsIframeLoading(true);
        }
    }, [visible]);

    return (
        <AnimatePresence>
            {visible && (
                <Overlay variants={overlayVariants} initial="hidden" animate="visible" exit="hidden">
                    <PaymentModalContainer variants={modalVariants}>
                        <AnimatePresence>
                            {isIframeLoading && (
                                <LoadingOverlay
                                    key="loader"
                                    initial={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Spinner />
                                </LoadingOverlay>
                            )}
                        </AnimatePresence>
                        <Iframe
                            src={url}
                            title="Secure Payment"
                            allow="payment"
                            onLoad={() => setIsIframeLoading(false)}
                            style={{ visibility: isIframeLoading ? 'hidden' : 'visible' }}
                        />
                    </PaymentModalContainer>
                </Overlay>
            )}
        </AnimatePresence>
    );
};

const EmailPromptModal = ({ visible, onClose, onSubmit, newEmail, setNewEmail }) => (
    <AnimatePresence>
        {visible && (
            <Overlay variants={overlayVariants} initial="hidden" animate="visible" exit="hidden">
                <ModalContainer
                    style={{maxWidth: '450px'}}
                    variants={modalVariants}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ModalHeader>
                        <ModalTitle> απαιτείται διεύθυνση ηλεκτρονικού ταχυδρομείου</ModalTitle>
                        <CloseButton onClick={onClose}>&times;</CloseButton>
                    </ModalHeader>
                    <ModalContent>
                        <p>Για την ολοκλήρωση της πληρωμής, παρακαλούμε δώστε μια έγκυρη διεύθυνση email. Οι λογαριασμοί Apple Private Relay δεν υποστηρίζονται.</p>
                        <EmailInput
                            type="email"
                            placeholder="your.email@example.com"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />
                    </ModalContent>
                    <ModalActions>
                        <GradientButton onClick={onSubmit} style={{width: 'auto', padding: '12px 24px'}}>
                           Υποβολή και Επανάληψη
                        </GradientButton>
                    </ModalActions>
                </ModalContainer>
            </Overlay>
        )}
    </AnimatePresence>
);

const ProfilePromptModal = ({
    visible,
    onClose,
    onSubmit,
    needsEmail,
    needsPhone,
    email,
    setEmail,
    phone,
    setPhone
}) => (
    <AnimatePresence>
        {visible && (
            <Overlay variants={overlayVariants} initial="hidden" animate="visible" exit="hidden">
                <ModalContainer
                    style={{ maxWidth: '460px' }}
                    variants={modalVariants}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ModalHeader>
                        <ModalTitle>Απαιτούνται στοιχεία επικοινωνίας</ModalTitle>
                        <CloseButton onClick={onClose}>&times;</CloseButton>
                    </ModalHeader>
                    <ModalContent>
                        <p>Για να συνεχίσετε, συμπληρώστε τα παρακάτω στοιχεία.</p>
                        {needsEmail && (
                            <EmailInput
                                type="email"
                                placeholder="your.email@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                inputMode="email"
                                maxLength={254}
                            />
                        )}
                        {needsPhone && (
                            <PhoneInput
                                type="tel"
                                placeholder="69XXXXXXXX"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                autoComplete="tel"
                                inputMode="tel"
                                maxLength={10}
                                pattern="^69[0-9]{8}$"
                            />
                        )}
                    </ModalContent>
                    <ModalActions>
                        <GradientButton onClick={onSubmit} style={{ width: 'auto', padding: '12px 24px' }}>
                            Αποθήκευση & Συνέχεια
                        </GradientButton>
                    </ModalActions>
                </ModalContainer>
            </Overlay>
        )}
    </AnimatePresence>
);

const SuccessMessage = ({ isTokenFromUrl }) => (
    <CenteredMessageContainer variants={containerVariants} initial="hidden" animate="visible" exit="hidden">
        <MessageContent>
            <IconCircle success>
                <i className="fas fa-check" style={{ color: '#fff', fontSize: '50px' }}></i>
            </IconCircle>
            <MessageTitle>Τα εισιτήρια σας αγοράστηκαν επιτυχώς</MessageTitle>
            <MessageText>
                {isTokenFromUrl
                    ? 'Μπορείτε να τα βρείτε είτε στην σελίδα "Bookings" της εφαρμογής είτε στο email σας.'
                    : 'Τα εισιτήρια σας, αγοράστηκαν επιτυχώς! Μπορείτε να τα βρείτε, είτε κατεβάζοντας την εφαρμογή μας, είτε στο email σας.'
                }
            </MessageText>
            {!isTokenFromUrl && (
                <GradientButton onClick={() => window.location.href = 'https://upass.gr/events'} style={{marginTop: '30px'}}>
                    Δες όλα τα events
                </GradientButton>
            )}
        </MessageContent>
    </CenteredMessageContainer>
);

const CancelledMessage = ({ onTryAgain }) => (
    <CenteredMessageContainer variants={containerVariants} initial="hidden" animate="visible" exit="hidden">
        <MessageContent>
            <IconCircle>
                <i className="fas fa-times" style={{ color: '#fff', fontSize: '50px' }}></i>
            </IconCircle>
            <MessageTitle>Η Πληρωμή Ακυρώθηκε</MessageTitle>
            <MessageText>
               Η διαδικασία πληρωμής ακυρώθηκε ή απέτυχε.
            </MessageText>
            <GradientButton onClick={onTryAgain} style={{marginTop: '30px'}}>
                Δοκιμάστε Ξανά
            </GradientButton>
        </MessageContent>
    </CenteredMessageContainer>
);


// --- MAIN EXPORT --- //
export default function PaymentPage({ eventId, code }) {
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventImage, setEventImage] = useState('');
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showCancelledMessage, setShowCancelledMessage] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [profileNeedsEmail, setProfileNeedsEmail] = useState(false);
  const [profileNeedsPhone, setProfileNeedsPhone] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [isTokenFromUrl, setIsTokenFromUrl] = useState(false);

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isAppleRelayEmail = (value) =>
    value && value.includes('@privaterelay.appleid.com');

  const normalizePhone = (value) => value.replace(/\D/g, '');

  const isValidPhone = (value) => {
    const normalized = normalizePhone(value);
    return /^69\d{8}$/.test(normalized);
  };

  const fetchTicketsAndEvent = async (activeToken) => {
    try {
      const resp = await fetch(`${PHP_BASE_URL}/getTickets2.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `event_id=${eventId}&loginToken=${activeToken}`
      });
      const json = await resp.json();
      if (!json.success || !json.tickets || json.tickets.length === 0) {
        window.location.href = 'https://upass.gr';
        return;
      }
      if (json.event) {
        setEventTitle(json.event.title);
        setEventImage(json.event.image);
      }
      setTickets(json.tickets.map(t => ({ ...t, selected: 0 })));
    } catch (err) {
      window.location.href = 'https://upass.gr';
    } finally {
      setLoadingTickets(false);
    }
  };

  const checkUserProfile = async (activeToken) => {
    try {
      const resp = await fetch(`${PHP_BASE_URL}/user_profile.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginToken: activeToken })
      });
      if (!resp.ok) {
        return false;
      }
      const data = await resp.json();
      if (!data.success) {
        return false;
      }
      if (data.needsEmail || data.needsPhone) {
        setProfileNeedsEmail(Boolean(data.needsEmail));
        setProfileNeedsPhone(Boolean(data.needsPhone));
        setProfileEmail(data.needsEmail ? '' : (data.email || ''));
        setProfilePhone(data.phone || '');
        setShowProfilePrompt(true);
        return true;
      }
    } catch (err) {
      return false;
    }
    return false;
  };

  const handleProfilePromptClose = () => {
    setPendingCheckout(false);
    const redirectUrl = `/account/${eventId}${code ? `/${code}` : ''}`;
    window.location.href = redirectUrl;
  };

  const handleProfileUpdate = async () => {
    if (!authToken) {
      alert('Σφάλμα ταυτοποίησης. Παρακαλώ συνδεθείτε ξανά.');
      return;
    }
    const trimmedEmail = profileEmail.trim();
    const trimmedPhone = profilePhone.trim();

    if (profileNeedsEmail) {
      if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
        alert('Παρακαλώ εισάγετε έγκυρο email.');
        return;
      }
      if (isAppleRelayEmail(trimmedEmail)) {
        alert('Τα emails Apple Private Relay δεν υποστηρίζονται.');
        return;
      }
    }

    if (profileNeedsPhone && !isValidPhone(trimmedPhone)) {
      alert('Παρακαλώ εισάγετε έγκυρο τηλέφωνο που ξεκινά με 69 και έχει 10 ψηφία.');
      return;
    }

    const payload = { loginToken: authToken };
    if (profileNeedsEmail) payload.email = trimmedEmail;
    if (profileNeedsPhone) payload.phone = normalizePhone(trimmedPhone);

    try {
      const resp = await fetch(`${PHP_BASE_URL}/update_profile.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.message || 'Αποτυχία ενημέρωσης στοιχείων.');
      }
      setShowProfilePrompt(false);
      setProfileNeedsEmail(false);
      setProfileNeedsPhone(false);
      if (pendingCheckout) {
        setPendingCheckout(false);
        initializePayment();
        return;
      }
      setLoadingTickets(true);
      await fetchTicketsAndEvent(authToken);
    } catch (err) {
      alert(err.message);
    }
  };

  // Load tickets and event data
  useEffect(() => {
    if (!eventId) {
      window.location.href = 'https://upass.gr';
      return;
    }

    const run = async () => {
      setLoadingTickets(true);
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      let activeToken = tokenFromUrl;

      if (tokenFromUrl) {
        setIsTokenFromUrl(true);
      }

      if (!activeToken) {
        activeToken = localStorage.getItem('logintoken');
      }

      if (!activeToken) {
        const redirectUrl = `/account/${eventId}${code ? `/${code}` : ''}`;
        window.location.href = redirectUrl;
        return;
      }

      setAuthToken(activeToken);

      await fetchTicketsAndEvent(activeToken);
    };

    run();
  }, [eventId, code]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      switch (event.data) {
        case 'paymentSuccess':
          setShowPaymentModal(false);
          setShowSuccessMessage(true);
          break;
        case 'paymentFailure':
        case 'paymentCancel':
          setShowPaymentModal(false);
          setShowCancelledMessage(true);
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const updateQty = (id, qty) =>
    setTickets(ts =>
      ts.map(t =>
        t.id === id ? { ...t, selected: Math.max(0, Math.min(qty, t.quantity)) } : t
      )
    );

  const totalPrice = tickets
    .reduce(
      (sum, t) => sum + t.selected * Number(t.price),
      0
    )
    .toFixed(2);

  const initializePayment = async () => {
    if (!authToken) {
      alert('Authentication error. Please log in again.');
      return;
    }
    setInitializing(true);
    try {
      const selected = tickets
        .filter(t => t.selected > 0)
        .map(t => ({ id: t.id, quantity: t.selected }));

      const body = {
        loginToken: authToken,
        eventId,
        tickets: selected,
        price: totalPrice * 100,
        paymentMethod: 'cardlink'
      };
      if (code) {
        body.code = code;
      }

      const resp = await fetch(`${PHP_BASE_URL}/cardlink.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await resp.json();

      if (!resp.ok) {
        if (data.error === 'apple_relay_email') {
            setShowEmailPrompt(true);
            throw new Error('apple_relay_email');
        }
        throw new Error(data.message || 'Payment setup failed');
      }

      if (!data.redirectUrl) throw new Error('Invalid payment setup');
      setPaymentUrl(data.redirectUrl);
      setShowPaymentModal(true);
    } catch (e) {
      if (e.message !== 'apple_relay_email') {
          alert(e.message);
      }
    } finally {
      setInitializing(false);
    }
  };

  const handleEmailUpdateAndRetry = async () => {
    if (!newEmail || !authToken) {
        alert('Please enter a valid email address.');
        return;
    }
    try {
        const resp = await fetch(`${PHP_BASE_URL}/update_email.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ loginToken: authToken, newEmail: newEmail })
        });
        const data = await resp.json();
        if (!data.success) {
            throw new Error(data.message || 'Failed to update email.');
        }

        alert('Email updated successfully! Retrying payment.');
        setShowEmailPrompt(false);
        setNewEmail('');
        initializePayment();

    } catch (e) {
        alert(`Error: ${e.message}`);
    }
  };


  const handleCheckoutClick = async () => {
    if (!agreedToPolicy) {
      alert('Πρέπει να συμφωνήσετε με την πολιτική ακυρώσεων και τους όρους χρήσης για να συνεχίσετε.');
      return;
    }
    if (!authToken) {
      alert('Σφάλμα ταυτοποίησης. Παρακαλώ συνδεθείτε ξανά.');
      return;
    }
    setPendingCheckout(true);
    const needsProfile = await checkUserProfile(authToken);
    if (needsProfile) {
      return;
    }
    setPendingCheckout(false);
    initializePayment();
  };

  const handleTryAgain = () => {
    setShowCancelledMessage(false);
  };

  if (loadingTickets && !showProfilePrompt) {
    return <CenteredMessageContainer>Φόρτωση Εισιτηρίων...</CenteredMessageContainer>;
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AnimatePresence mode="wait">
        {showSuccessMessage ? (
          <SuccessMessage key="success" isTokenFromUrl={isTokenFromUrl} />
        ) : showCancelledMessage ? (
          <CancelledMessage key="cancelled" onTryAgain={handleTryAgain} />
        ) : (
          <PageWrapper key="payment">
            <Navbar />
            <Header variants={itemVariants} initial="hidden" animate="visible">
                <EventImage
                    src={eventImage}
                    alt={eventTitle}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                />
                <EventTitle
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    {eventTitle}
                </EventTitle>
            </Header>
            <ContentWrapper variants={containerVariants} initial="hidden" animate="visible">
                <GlassCard variants={itemVariants}>
                    {tickets.map(t => (
                        <TicketRow key={t.id} ticket={t} onChange={updateQty} />
                    ))}
                    <TotalRow>
                        <TotalLabel>Σύνολο</TotalLabel>
                        <TotalValue>{totalPrice} €</TotalValue>
                    </TotalRow>
                </GlassCard>

                <GlassCard variants={itemVariants} style={{ marginTop: '24px' }}>
                    <PolicyInfoContainer>
                        
                        <p>Δεν επιτρέπονται οι ακυρώσεις.</p>
                        <p>Δεν επιτρέπονται οι αλλαγές.</p>
                        <StyledLink onClick={(e) => { e.preventDefault(); setShowCancellationPolicy(true); }} style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
                            Πολιτική Ακυρώσεων
                        </StyledLink>
                    </PolicyInfoContainer>
                    <CheckboxContainer>
                        <CheckboxLabel>
                            Συμφωνώ με την{' '}
                            <StyledLink onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowCancellationPolicy(true); }} style={{display: 'inline'}}>
                                πολιτική ακυρώσεων
                            </StyledLink>
                            {' '}και με τους{' '}
                            <StyledLink onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsOfUse(true); }} style={{display: 'inline'}}>
                                όρους χρήσης
                            </StyledLink>.
                        </CheckboxLabel>
                        <HiddenCheckbox
                            id="policy-agree"
                            checked={agreedToPolicy}
                            onChange={() => setAgreedToPolicy(!agreedToPolicy)}
                        />
                        <CustomCheckbox checked={agreedToPolicy}>
                            <AnimatePresence>
                                {agreedToPolicy && (
                                    <motion.i
                                        className="fas fa-check"
                                        style={{ color: '#fff', fontSize: '14px' }}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                    />
                                )}
                            </AnimatePresence>
                        </CustomCheckbox>
                    </CheckboxContainer>
                    <GradientButton
                        onClick={handleCheckoutClick}
                        disabled={totalPrice <= 0 || initializing || !agreedToPolicy}
                        style={{ marginTop: 24 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {initializing ? (
                            'Φόρτωση…'
                        ) : (
                            <>
                                <i className="fas fa-credit-card"></i>
                                Ολοκλήρωση Πληρωμής
                            </>
                        )}
                    </GradientButton>
                </GlassCard>
            </ContentWrapper>
          </PageWrapper>
        )}
      </AnimatePresence>
      <CancellationPolicyModal
        visible={showCancellationPolicy}
        onClose={() => setShowCancellationPolicy(false)}
      />
       <TermsOfUseModal
        visible={showTermsOfUse}
        onClose={() => setShowTermsOfUse(false)}
      />
      <PaymentModal
        visible={showPaymentModal}
        url={paymentUrl}
        onClose={() => setShowPaymentModal(false)}
      />
       <EmailPromptModal
        visible={showEmailPrompt}
        onClose={() => setShowEmailPrompt(false)}
        onSubmit={handleEmailUpdateAndRetry}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
      />
      <ProfilePromptModal
        visible={showProfilePrompt}
        onClose={handleProfilePromptClose}
        onSubmit={handleProfileUpdate}
        needsEmail={profileNeedsEmail}
        needsPhone={profileNeedsPhone}
        email={profileEmail}
        setEmail={setProfileEmail}
        phone={profilePhone}
        setPhone={setProfilePhone}
      />
    </ThemeProvider>
  );
}